/**
 * Prioris — Materials Approval: spreadsheet-style editable grid.
 *
 * Design: dense table, click-to-edit cells, Tab moves right, Enter moves down,
 * + Row appends a blank row, bulk actions on selected rows, sortable columns.
 * Matches the Prioris dark theme (11px base font).
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus, Trash2, Filter, ChevronDown, ChevronUp, Loader2, Hash,
  Check, X, Pencil, RefreshCw, AlertCircle, ExternalLink, MessageSquare,
  FileUp, Sparkles, Settings
} from 'lucide-react';
import MaSubmittalDrawer from './MaSubmittalDrawer';
import MaFamilyFieldsModal from './MaFamilyFieldsModal';

const STATUSES = ['Draft', 'Under Review', 'Action Required', 'Approved', 'Rejected', 'Superseded', 'Cancelled'];
const COMPLIANCE = ['Pending', 'Pass', 'Partial', 'Fail'];

const STATUS_COLOURS = {
  'Draft':           { bg: 'rgba(148,163,184,0.15)',  fg: '#94a3b8' },
  'Under Review':    { bg: 'rgba(59,130,246,0.18)',   fg: '#60a5fa' },
  'Action Required': { bg: 'rgba(245,158,11,0.18)',   fg: '#fbbf24' },
  'Approved':        { bg: 'rgba(16,185,129,0.18)',   fg: '#34d399' },
  'Rejected':        { bg: 'rgba(239,68,68,0.18)',    fg: '#f87171' },
  'Superseded':      { bg: 'rgba(168,85,247,0.15)',   fg: '#c084fc' },
  'Cancelled':       { bg: 'rgba(100,116,139,0.15)',  fg: '#94a3b8' },
};

const COMPLIANCE_COLOURS = {
  'Pending': '#94a3b8', 'Pass': '#34d399', 'Partial': '#fbbf24', 'Fail': '#f87171',
};

// Column definitions — order = display order. `key` maps to the DB field.
// NOTE: Section and Clause are positioned right after Internal Ref so users
// can pre-populate the RAM header inline without opening the drawer. They
// cascade: picking a Section filters the Clause dropdown to that section's
// clauses only and, on commit, writes {material_family, spec_table_key,
// spec_rubrica_code} onto the submittal in a single PATCH.
const COLUMNS = [
  { key: 'seq',                label: '#',            width: 36,  readonly: true,  type: 'number' },
  { key: 'internal_ref',       label: 'Internal Ref', width: 110, readonly: true,  type: 'text'   },
  { key: 'section_key',        label: 'Section',      width: 140, readonly: false, type: 'section' },
  { key: 'spec_rubrica_code',  label: 'Clause',       width: 100, readonly: false, type: 'clause'  },
  { key: 'external_ref',       label: 'External Ref', width: 110, readonly: false, type: 'text'   },
  { key: 'product_description',label: 'Product',      width: 180, readonly: false, type: 'text'   },
  { key: 'producer',           label: 'Producer',     width: 140, readonly: false, type: 'text'   },
  { key: 'sln_location',       label: 'Location (Zone → Element)', width: 190, readonly: true,  type: 'location' },
  { key: 'asset_zone',         label: 'Zone / Use (free)',   width: 110, readonly: false, type: 'text'   },
  { key: 'design_requirement', label: 'Requirement',  width: 170, readonly: false, type: 'text'   },
  { key: 'applicable_standard',label: 'Standard',     width: 130, readonly: false, type: 'standard' },
  { key: 'revision',           label: 'Rev',          width: 50,  readonly: false, type: 'text'   },
  { key: 'date_submitted',     label: 'Submitted',    width: 110, readonly: false, type: 'date'   },
  { key: 'status',             label: 'Status',       width: 120, readonly: false, type: 'status' },
  { key: 'assigned_engineer',  label: 'Engineer',     width: 130, readonly: false, type: 'text'   },
  { key: 'compliance_result',  label: 'Result',       width: 80,  readonly: false, type: 'compliance' },
  { key: 'notes',              label: 'Notes',        width: 180, readonly: false, type: 'text'   },
];

export default function MaSubmittalsGrid({ t, project, onBack }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const projectId = project?.id;
  const [rows, setRows] = useState([]);
  const [families, setFamilies] = useState([]);
  const [standards, setStandards] = useState([]);
  const [locationTree, setLocationTree] = useState([]);   // [{zone_id, zone_name, elements:[...]}]
  const [locationPickerRow, setLocationPickerRow] = useState(null); // row being edited
  const [projectSpecSelections, setProjectSpecSelections] = useState([]); // [{table_key, section_key, rubrica_details:[...]}]
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState({ key: 'seq', dir: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClause, setFilterClause] = useState('');
  const [editing, setEditing] = useState(null); // {id, key}
  const [refConfig, setRefConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const inputRef = useRef(null);

  // ── load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [submittals, fams, stds, cfg, loc, specSels] = await Promise.all([
        fetchSln(`/api/prioris/ma/submittals?project_id=${projectId}`),
        fetchSln('/api/prioris/ma/families'),
        fetchSln('/api/prioris/ma/standards'),
        fetchSln(`/api/prioris/ma/ref-config?project_id=${projectId}`),
        fetchSln(`/api/prioris/ma/location-tree?project_id=${projectId}`),
        fetchSln(`/api/prioris/spec-catalog/project/${projectId}/selections`),
      ]);
      setRows(submittals.submittals || []);
      setFamilies(fams.families || []);
      setStandards(stds.standards || []);
      setRefConfig(cfg);
      setLocationTree(loc.zones || []);
      setProjectSpecSelections(specSels.selections || []);
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  // ── cascade mapping from project spec selections ─────────────────────────
  // Build:
  //   sectionsList = [{ section_key, label, tables: [{table_key, family, rubricas: [{code,label}]}] }]
  //   rubricaIndex = { [rubrica_code]: { table_key, section_key, family } }
  //   tableIndex   = { [table_key]:   { section_key, family } }
  const { sectionsList, rubricaIndex, tableIndex } = useMemo(() => {
    const secMap = {};
    const rIdx = {};
    const tIdx = {};
    for (const sel of projectSpecSelections) {
      const sk = sel.section_key || sel.table_key || '—';
      const s = (secMap[sk] = secMap[sk] || { section_key: sk, label: sk, tables: [] });
      s.tables.push({
        table_key: sel.table_key, family: sel.family,
        rubricas: sel.rubrica_details || [],
      });
      tIdx[sel.table_key] = { section_key: sk, family: sel.family };
      for (const r of (sel.rubrica_details || [])) {
        // Only keep the FIRST table that owns a given clause code per
        // section. This way committing clause 552-AA always resolves to
        // FP24-552-1 (primary) even though FP24-552-2 also lists 552-AA;
        // the compliance card renders both tables automatically via the
        // multi-matching logic in SpecCatalogComplianceCard.
        if (!rIdx[r.code]) {
          rIdx[r.code] = { table_key: sel.table_key, section_key: sk, family: sel.family };
        }
      }
    }
    return {
      sectionsList: Object.values(secMap).sort((a, b) => a.section_key.localeCompare(b.section_key)),
      rubricaIndex: rIdx,
      tableIndex: tIdx,
    };
  }, [projectSpecSelections]);

  // Row → section_key resolver (uses row.spec_table_key primarily, else infers
  // from spec_rubrica_code). Falls back to '' so the grid shows '—' instead
  // of leaking the raw material_family.
  const rowSectionKey = useCallback((row) => {
    if (!row) return '';
    if (row.spec_table_key && tableIndex[row.spec_table_key]) return tableIndex[row.spec_table_key].section_key;
    if (row.spec_rubrica_code && rubricaIndex[row.spec_rubrica_code]) return rubricaIndex[row.spec_rubrica_code].section_key;
    return '';
  }, [tableIndex, rubricaIndex]);

  // Within a single section multiple tables may share clause codes
  // (e.g. FP24-552-1 and FP24-552-2 both list 552-A/552-B/552-AA/etc.
  // because 552-2 mirrors 552-1 on purpose). The de-duplicated list below
  // shows each clause code only once per section. The compliance card
  // automatically renders ALL matching tables per family, so picking a
  // de-duplicated code still evaluates against both tables.
  const sectionClauses = useCallback((sectionKey) => {
    if (!sectionKey) return [];
    const sec = sectionsList.find(s => s.section_key === sectionKey);
    if (!sec) return [];
    const seen = new Set();
    const out = [];
    for (const tb of sec.tables) {
      for (const r of tb.rubricas) {
        if (seen.has(r.code)) continue;
        seen.add(r.code);
        out.push(r);
      }
    }
    return out;
  }, [sectionsList]);

  // ── filter + sort ───────────────────────────────────────────────────────
  const visible = useMemo(() => {
    let list = [...rows];
    if (filterSection) list = list.filter(r => rowSectionKey(r) === filterSection);
    if (filterStatus) list = list.filter(r => r.status === filterStatus);
    if (filterClause) list = list.filter(r => (r.spec_rubrica_code || '').startsWith(filterClause));
    if (filterText) {
      const q = filterText.toLowerCase();
      list = list.filter(r =>
        Object.values(r).some(v =>
          typeof v === 'string' && v.toLowerCase().includes(q)
        )
      );
    }
    const { key, dir } = sort;
    list.sort((a, b) => {
      const av = key === 'section_key' ? rowSectionKey(a) : (a[key] ?? '');
      const bv = key === 'section_key' ? rowSectionKey(b) : (b[key] ?? '');
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [rows, filterSection, filterStatus, filterText, filterClause, sort, rowSectionKey]);

  const familyLabel = useCallback(key => families.find(f => f.key === key)?.label || key, [families]);

  // ── row operations ──────────────────────────────────────────────────────
  const addRow = async () => {
    try {
      // When the user is filtering by a specific section, seed the new row
      // with the first table / family of that section so the sidebar and
      // Clause dropdown are already meaningful.
      let family = 'concrete_mix';
      let spec_table_key = null;
      if (filterSection) {
        const sec = sectionsList.find(s => s.section_key === filterSection);
        if (sec?.tables?.[0]) {
          family = sec.tables[0].family || 'concrete_mix';
          spec_table_key = sec.tables[0].table_key;
        }
      }
      const row = await fetchSln('/api/prioris/ma/submittals', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, material_family: family, spec_table_key }),
      });
      setRows(prev => [...prev, row]);
      setEditing({ id: row.id, key: 'section_key' });
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const patchRow = async (id, patch) => {
    try {
      const updated = await fetchSln(`/api/prioris/ma/submittals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      setRows(prev => prev.map(r => r.id === id ? updated : r));
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const deleteRow = async (id) => {
    try {
      await fetchSln(`/api/prioris/ma/submittals/${id}`, { method: 'DELETE' });
      setRows(prev => prev.filter(r => r.id !== id));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} submittal(s)?`)) return;
    try {
      await fetchSln('/api/prioris/ma/submittals/bulk', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selected), action: 'delete' }),
      });
      setRows(prev => prev.filter(r => !selected.has(r.id)));
      setSelected(new Set());
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const bulkStatus = async (status) => {
    if (selected.size === 0) return;
    try {
      await fetchSln('/api/prioris/ma/submittals/bulk', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selected), action: 'set_status', value: status }),
      });
      setRows(prev => prev.map(r => selected.has(r.id) ? { ...r, status } : r));
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(prev =>
    prev.size === visible.length ? new Set() : new Set(visible.map(r => r.id))
  );

  // ── cell editing (Tab / Enter / Esc) ────────────────────────────────────
  const moveEdit = (rowId, key, dir) => {
    const rowIdx = visible.findIndex(r => r.id === rowId);
    const colIdx = COLUMNS.findIndex(c => c.key === key);
    if (dir === 'right') {
      for (let i = colIdx + 1; i < COLUMNS.length; i++) {
        if (!COLUMNS[i].readonly) { setEditing({ id: rowId, key: COLUMNS[i].key }); return; }
      }
      setEditing(null);
    } else if (dir === 'down') {
      const next = visible[rowIdx + 1];
      if (next) setEditing({ id: next.id, key });
      else setEditing(null);
    }
  };

  const commitCell = (rowId, key, value) => {
    const existing = rows.find(r => r.id === rowId);
    if (!existing) { setEditing(null); return; }

    // ── Cascading writes for Section + Clause ──────────────────────────
    // Picking a Section clears the clause (user must re-pick) and pre-sets
    // spec_table_key to the first table of that section. Material_family is
    // also updated so the sidebar and compliance card stay consistent.
    if (key === 'section_key') {
      if (rowSectionKey(existing) === value) { setEditing(null); return; }
      const sec = sectionsList.find(s => s.section_key === value);
      const firstTable = sec?.tables?.[0];
      const patch = {
        spec_table_key: firstTable ? firstTable.table_key : null,
        spec_rubrica_code: null,
        material_family: firstTable?.family || existing.material_family || 'concrete_mix',
      };
      patchRow(rowId, patch);
      setEditing(null);
      return;
    }

    // Picking a Clause — look up its owning table / section and apply the
    // complete header (table + rubrica + family) in one PATCH.
    if (key === 'spec_rubrica_code') {
      if (existing.spec_rubrica_code === value) { setEditing(null); return; }
      const meta = rubricaIndex[value];
      const patch = { spec_rubrica_code: value || null };
      if (meta) {
        patch.spec_table_key = meta.table_key;
        patch.material_family = meta.family || existing.material_family || 'concrete_mix';
      }
      patchRow(rowId, patch);
      setEditing(null);
      return;
    }

    if (existing[key] === value) { setEditing(null); return; }
    patchRow(rowId, { [key]: value });
    setEditing(null);
  };

  // ── ref-config editor ───────────────────────────────────────────────────
  const saveRefConfig = async (next) => {
    setSavingConfig(true);
    try {
      const cfg = await fetchSln('/api/prioris/ma/ref-config', {
        method: 'PUT',
        body: JSON.stringify({ project_id: projectId, ...next }),
      });
      setRefConfig(cfg);
      setEditingConfig(false);
      setMsg({ kind: 'ok', text: 'Reference format updated. New rows will use the new format.' });
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
    setSavingConfig(false);
  };

  // ── render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="px-6 py-5" data-testid="prioris-ma-grid-loading">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} />
      </div>
    );
  }

  return (
    <div className="px-5 py-4" data-testid="prioris-ma-grid" style={{ color: t.text }}>
      {/* Header */}
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-[11px] font-semibold tracking-wider" style={{ color: t.accent }}>MATERIALS APPROVAL</span>
        <span className="text-[11px]" style={{ color: t.textDim }}>· {project.asset_name || project.name}</span>
      </div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="text-base font-bold" style={{ color: t.text }}>Submittals Log</h2>
        <RefConfigChip t={t} cfg={refConfig}
          editing={editingConfig}
          onStart={() => setEditingConfig(true)}
          onSave={saveRefConfig}
          onCancel={() => setEditingConfig(false)}
          saving={savingConfig} />
        <button onClick={load} className="p-1 rounded hover:opacity-70" title="Reload" data-testid="ma-reload">
          <RefreshCw className="w-3 h-3" style={{ color: t.textMuted }} />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap text-[11px]">
        <input
          placeholder="Filter…"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, width: 140 }}
          data-testid="ma-filter-text"
        />
        <select value={filterSection} onChange={e => setFilterSection(e.target.value)}
          className="rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
          data-testid="ma-filter-section">
          <option value="">All sections</option>
          {sectionsList.map(s => <option key={s.section_key} value={s.section_key}>{s.section_key}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
          data-testid="ma-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          placeholder="Clause prefix (e.g. 14.03.6)"
          value={filterClause}
          onChange={e => setFilterClause(e.target.value)}
          className="rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, width: 160 }}
          data-testid="ma-filter-clause"
        />

        <span className="text-[11px] ml-auto" style={{ color: t.textDim }}>
          {visible.length} of {rows.length}{selected.size > 0 && ` · ${selected.size} selected`}
        </span>

        {selected.size > 0 && (
          <>
            <select onChange={e => { if (e.target.value) { bulkStatus(e.target.value); e.target.value = ''; } }}
              className="rounded px-2 py-1 text-[11px]"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="ma-bulk-status">
              <option value="">Set status…</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={bulkDelete}
              className="px-2 py-1 rounded text-[11px] flex items-center gap-1"
              style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171' }}
              data-testid="ma-bulk-delete">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </>
        )}

        <button onClick={addRow}
          className="px-2 py-1 rounded text-[11px] flex items-center gap-1 font-semibold"
          style={{ background: t.accent, color: '#000' }}
          data-testid="ma-add-row">
          <Plus className="w-3 h-3" /> Row
        </button>

        <button onClick={() => setShowPdfModal(true)}
          className="px-2 py-1 rounded text-[11px] flex items-center gap-1"
          style={{ background: t.inputBg, border: `1px dashed ${t.cardBorder}`, color: t.textMuted }}
          title="Auto-populate fields from a PDF submittal — coming soon"
          data-testid="ma-ingest-pdf">
          <FileUp className="w-3 h-3" /> Ingest PDF <Sparkles className="w-2.5 h-2.5" style={{ color: t.accent }} />
        </button>

        {isAdmin && (
          <button onClick={() => setShowFieldsModal(true)}
            className="px-2 py-1 rounded text-[11px] flex items-center gap-1"
            style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}
            title="Manage custom fields per material type (admin)"
            data-testid="ma-manage-fields">
            <Settings className="w-3 h-3" /> Fields
          </button>
        )}
      </div>

      {/* Toast */}
      {msg && (
        <div className="mb-2 px-2 py-1 text-[11px] rounded flex items-center gap-2"
          style={{
            background: msg.kind === 'err' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
            color: msg.kind === 'err' ? '#f87171' : t.accent,
          }}>
          {msg.kind === 'err' && <AlertCircle className="w-3 h-3" />}
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)} className="ml-auto opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      {/* Empty state */}
      {rows.length === 0 && (
        <div className="border border-dashed rounded-lg py-12 text-center"
          style={{ borderColor: t.cardBorder }} data-testid="ma-empty-state">
          <Hash className="w-8 h-8 mx-auto mb-3" style={{ color: t.textDim }} />
          <h3 className="text-sm font-semibold mb-1" style={{ color: t.text }}>No submittals yet</h3>
          <p className="text-[12px] mb-4" style={{ color: t.textDim }}>
            Add materials for approval — mixes, cement, aggregates, rebar, and more.<br />
            Each row is one submittal. Click the button below to get started.
          </p>
          <button onClick={addRow}
            className="px-3 py-1.5 rounded text-[12px] font-semibold inline-flex items-center gap-1"
            style={{ background: t.accent, color: '#000' }}
            data-testid="ma-empty-add">
            <Plus className="w-3 h-3" /> Add first submittal
          </button>
        </div>
      )}

      {/* Grid */}
      {rows.length > 0 && (
        <div className="overflow-x-auto border rounded" style={{ borderColor: t.cardBorder }}>
          <table className="text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
            <thead>
              <tr style={{ background: t.inputBg }}>
                <th className="sticky left-0 z-10 px-1 py-1 text-left" style={{ background: t.inputBg, borderBottom: `1px solid ${t.cardBorder}`, width: 24 }}>
                  <input type="checkbox" checked={selected.size === visible.length && visible.length > 0}
                    onChange={toggleAll} data-testid="ma-select-all" />
                </th>
                {COLUMNS.map(col => (
                  <th key={col.key}
                    onClick={() => setSort(s => ({ key: col.key, dir: s.key === col.key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                    className="px-2 py-1.5 text-left font-semibold cursor-pointer select-none"
                    style={{ color: t.textMuted, borderBottom: `1px solid ${t.cardBorder}`, width: col.width, minWidth: col.width }}>
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sort.key === col.key && (sort.dir === 'asc' ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />)}
                    </div>
                  </th>
                ))}
                <th style={{ borderBottom: `1px solid ${t.cardBorder}`, width: 28 }} />
              </tr>
            </thead>
            <tbody>
              {visible.map((row, ri) => (
                <tr key={row.id} data-testid={`ma-row-${row.internal_ref}`}
                  style={{
                    background: selected.has(row.id) ? 'rgba(16,185,129,0.07)' : ri % 2 ? 'transparent' : 'rgba(255,255,255,0.015)'
                  }}>
                  <td className="sticky left-0 z-10 px-1 py-0.5" style={{ background: selected.has(row.id) ? 'rgba(16,185,129,0.07)' : t.bg, borderBottom: `1px solid ${t.cardBorder}` }}>
                    <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                  </td>
                  {COLUMNS.map(col => (
                    <GridCell
                      key={col.key}
                      t={t}
                      col={col}
                      row={row}
                      families={families}
                      standards={standards}
                      familyLabel={familyLabel}
                      sectionsList={sectionsList}
                      rubricaIndex={rubricaIndex}
                      rowSectionKey={rowSectionKey}
                      sectionClauses={sectionClauses}
                      editing={editing?.id === row.id && editing?.key === col.key}
                      onEdit={() => !col.readonly && setEditing({ id: row.id, key: col.key })}
                      onOpenRef={() => setDrawerRow(row)}
                      onOpenLocation={() => setLocationPickerRow(row)}
                      onCommit={(val) => commitCell(row.id, col.key, val)}
                      onCancel={() => setEditing(null)}
                      onTab={() => moveEdit(row.id, col.key, 'right')}
                      onEnter={() => moveEdit(row.id, col.key, 'down')}
                      inputRef={inputRef}
                    />
                  ))}
                  <td className="px-1 whitespace-nowrap" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                    <button onClick={() => setDrawerRow(row)}
                      className="p-1 rounded hover:bg-white/5"
                      title="Open details / comments / mix design"
                      data-testid={`ma-open-${row.internal_ref}`}>
                      <MessageSquare className="w-2.5 h-2.5" style={{ color: t.accent }} />
                    </button>
                    <button onClick={() => deleteRow(row.id)}
                      className="p-1 rounded hover:bg-red-500/10"
                      title="Delete row"
                      data-testid={`ma-del-${row.internal_ref}`}>
                      <Trash2 className="w-2.5 h-2.5" style={{ color: '#f87171' }} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={COLUMNS.length + 2}
                  className="px-3 py-1.5 text-[11px] cursor-pointer hover:opacity-80"
                  style={{ color: t.accent, background: t.inputBg }}
                  onClick={addRow}
                  data-testid="ma-add-row-footer">
                  <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add row</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {/* Location picker modal */}
      {locationPickerRow && (
        <LocationPickerModal
          t={t}
          row={locationPickerRow}
          tree={locationTree}
          projectId={projectId}
          onClose={() => setLocationPickerRow(null)}
          onTreeUpdated={async () => {
            try {
              const loc = await fetchSln(`/api/prioris/ma/location-tree?project_id=${projectId}`);
              setLocationTree(loc.zones || []);
            } catch {}
          }}
          onSave={async (patch) => {
            await patchRow(locationPickerRow.id, patch);
            setLocationPickerRow(null);
          }}
        />
      )}

      {/* Detail drawer */}
      {drawerRow && (
        <MaSubmittalDrawer
          t={t}
          submittal={drawerRow}
          onClose={() => setDrawerRow(null)}
          onPatched={(fresh) => { setRows(prev => prev.map(r => r.id === fresh.id ? fresh : r)); setDrawerRow(fresh); }}
        />
      )}

      {/* Custom-fields admin modal */}
      {showFieldsModal && (
        <MaFamilyFieldsModal
          t={t}
          onClose={() => setShowFieldsModal(false)}
          onSaved={async () => {
            try {
              const fams = await fetchSln('/api/prioris/ma/families');
              setFamilies(fams.families || []);
            } catch {}
          }}
        />
      )}

      {/* "Ingest PDF (coming soon)" modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowPdfModal(false)} data-testid="ma-pdf-modal">
          <div className="rounded-lg p-5 max-w-md" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <FileUp className="w-4 h-4" style={{ color: t.accent }} />
              <h3 className="text-sm font-bold">Ingest PDF — coming soon</h3>
              <Sparkles className="w-3 h-3" style={{ color: t.accent }} />
            </div>
            <p className="text-[12px] mb-3" style={{ color: t.textMuted }}>
              Upload a supplier's PDF submittal (mix design, rebar mill cert, aggregate data sheet, etc.) and the platform will
              auto-populate the submittal row fields — producer, cement type, SCM %, w/c ratio, declared strengths, standards —
              using OCR + an LLM extractor trained on concrete / steel / asphalt data sheets.
            </p>
            <ul className="text-[11px] space-y-1 mb-4" style={{ color: t.textDim }}>
              <li>• Drag-and-drop PDF or scanned image</li>
              <li>• Structured extraction into the existing columns + mix design form</li>
              <li>• Human-in-the-loop review step before anything is saved</li>
              <li>• Attach original PDF link for audit traceability</li>
            </ul>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px]" style={{ color: t.textDim }}>Target release: Q2 2026</span>
              <button onClick={() => setShowPdfModal(false)}
                className="px-3 py-1 rounded text-[11px] font-semibold"
                style={{ background: t.accent, color: '#000' }}
                data-testid="ma-pdf-modal-close">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function GridCell({ t, col, row, families, standards, familyLabel, sectionsList, rubricaIndex, rowSectionKey, sectionClauses, editing, onEdit, onOpenRef, onOpenLocation, onCommit, onCancel, onTab, onEnter, inputRef }) {
  const value = col.key === 'section_key'
    ? (rowSectionKey ? rowSectionKey(row) : '')
    : (row[col.key] ?? '');

  const cellStyle = {
    borderBottom: `1px solid ${t.cardBorder}`,
    borderRight: `1px solid ${t.cardBorder}`,
    verticalAlign: 'middle',
    maxWidth: col.width,
  };

  // Read-only rendering
  if (!editing) {
    let content;
    if (col.type === 'status') {
      const c = STATUS_COLOURS[value] || STATUS_COLOURS.Draft;
      content = <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap" style={{ background: c.bg, color: c.fg }}>{value}</span>;
    } else if (col.type === 'compliance') {
      content = <span className="font-semibold" style={{ color: COMPLIANCE_COLOURS[value] || t.textMuted }}>{value}</span>;
    } else if (col.type === 'family') {
      content = <span style={{ color: t.text }}>{familyLabel(value)}</span>;
    } else if (col.type === 'section') {
      // Section column: show section_key if set, else '—' hint to click
      content = value
        ? <span className="font-mono" style={{ color: t.text }}>{value}</span>
        : <span style={{ color: t.textDim }}>— click to set —</span>;
    } else if (col.type === 'clause') {
      content = value
        ? <span className="font-mono" style={{ color: t.text }}>{value}</span>
        : <span style={{ color: t.textDim }}>— click to set —</span>;
    } else if (col.type === 'number') {
      content = <span style={{ color: t.textMuted, fontFamily: 'monospace' }}>{value}</span>;
    } else if (col.key === 'internal_ref') {
      // Clickable — opens detail drawer
      return (
        <td className="px-2 py-1 truncate cursor-pointer"
          style={cellStyle}
          onClick={onOpenRef}
          data-testid={`ma-cell-${col.key}-${row.internal_ref}`}
          title="Open details · comments · mix design">
          <span className="font-semibold underline underline-offset-2 decoration-dotted" style={{ color: t.accent }}>{value}</span>
          {row.mix_design && Object.keys(row.mix_design).length > 0 && (
            <span className="ml-1 text-[9px]" style={{ color: t.textDim }} title="Mix design attached">●</span>
          )}
        </td>
      );
    } else if (col.type === 'location') {
      const has = !!row.sln_element_id || !!row.sln_zone_id;
      return (
        <td className="px-2 py-1 truncate cursor-pointer"
          style={cellStyle}
          onClick={onOpenLocation}
          data-testid={`ma-cell-location-${row.internal_ref}`}
          title="Click to link to a Service Life zone / element">
          {has ? (
            <span className="text-[11px]" style={{ color: t.text }}>
              <span style={{ color: t.accent }}>{row.sln_zone_name || '—'}</span>
              {row.sln_element_name && <span style={{ color: t.textMuted }}> → {row.sln_element_name}</span>}
            </span>
          ) : (
            <span className="text-[11px]" style={{ color: t.textDim }}>— click to add —</span>
          )}
        </td>
      );
    } else if (col.key === 'external_ref' && typeof value === 'string' && /^(https?:\/\/|www\.)/i.test(value)) {
      // Render external URL as clickable link
      const href = value.startsWith('http') ? value : `https://${value}`;
      return (
        <td className="px-2 py-1 truncate"
          style={cellStyle}
          onDoubleClick={onEdit}
          data-testid={`ma-cell-${col.key}-${row.internal_ref}`}
          title={`${value}\n(double-click to edit)`}>
          <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: t.accent }}>
            {value} <ExternalLink className="w-2 h-2 flex-shrink-0" />
          </a>
        </td>
      );
    } else {
      content = <span style={{ color: value ? t.text : t.textDim }}>{value || '—'}</span>;
    }
    return (
      <td className="px-2 py-1 truncate cursor-text"
        style={cellStyle}
        onClick={onEdit}
        data-testid={`ma-cell-${col.key}-${row.internal_ref}`}
        title={typeof value === 'string' ? value : ''}>
        {content}
      </td>
    );
  }

  // Editor
  const handleKey = (e) => {
    if (e.key === 'Enter')       { e.preventDefault(); onCommit(e.target.value); setTimeout(onEnter, 0); }
    else if (e.key === 'Tab')    { e.preventDefault(); onCommit(e.target.value); setTimeout(onTab, 0); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  const commonInputStyle = {
    background: t.inputBg,
    border: `1px solid ${t.accent}`,
    color: t.text,
    fontSize: 12,
    width: '100%',
    padding: '2px 4px',
    outline: 'none',
  };
  // Native <option> elements need explicit bg+color otherwise Chromium paints
  // them with the OS widget background (white in dark mode → unreadable).
  const optStyle = { background: t.inputBg, color: t.text };

  let editor;
  if (col.type === 'status') {
    editor = (
      <select ref={inputRef} defaultValue={value} onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle}>
        {STATUSES.map(s => <option key={s} value={s} style={optStyle}>{s}</option>)}
      </select>
    );
  } else if (col.type === 'compliance') {
    editor = (
      <select ref={inputRef} defaultValue={value} onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle}>
        {COMPLIANCE.map(s => <option key={s} value={s} style={optStyle}>{s}</option>)}
      </select>
    );
  } else if (col.type === 'family') {
    editor = (
      <select ref={inputRef} defaultValue={value} onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle}>
        {families.map(f => <option key={f.key} value={f.key} style={optStyle}>{f.label}</option>)}
      </select>
    );
  } else if (col.type === 'section') {
    editor = (
      <ThemedSelect t={t}
        value={value}
        options={[{ code: '', label: '—' }, ...(sectionsList || []).map(s => ({ code: s.section_key, label: s.section_key }))]}
        onCommit={onCommit}
        onCancel={onCancel}
        testid={`ma-edit-section-${row.internal_ref}`}
      />
    );
  } else if (col.type === 'clause') {
    const currentSection = rowSectionKey ? rowSectionKey(row) : '';
    let clauseOpts = [];
    if (currentSection && sectionClauses) {
      clauseOpts = sectionClauses(currentSection);
    } else {
      // De-duplicate global clause list by code
      const seen = new Set();
      Object.keys(rubricaIndex || {}).forEach(code => {
        if (!seen.has(code)) { seen.add(code); clauseOpts.push({ code, label: code }); }
      });
    }
    editor = (
      <ThemedSelect t={t}
        value={value}
        options={[{ code: '', label: '—' }, ...clauseOpts.map(r => ({ code: r.code, label: `${r.code} · ${r.label}` }))]}
        onCommit={onCommit}
        onCancel={onCancel}
        testid={`ma-edit-clause-${row.internal_ref}`}
      />
    );
  } else if (col.type === 'standard') {
    const matched = standards.filter(s => !row.material_family || !s.families?.length || s.families.includes(row.material_family));
    editor = (
      <>
        <input ref={inputRef} defaultValue={value} list={`std-list-${row.id}`}
          onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle} />
        <datalist id={`std-list-${row.id}`}>
          {matched.map(s => <option key={s.code} value={s.code}>{s.title}</option>)}
        </datalist>
      </>
    );
  } else if (col.type === 'date') {
    editor = (
      <input ref={inputRef} type="date" defaultValue={value || ''}
        onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle} />
    );
  } else {
    editor = (
      <input ref={inputRef} type="text" defaultValue={value}
        onBlur={e => onCommit(e.target.value)} onKeyDown={handleKey} style={commonInputStyle} />
    );
  }

  return <td className="px-1 py-0.5" style={cellStyle}>{editor}</td>;
}

function RefConfigChip({ t, cfg, editing, onStart, onSave, onCancel, saving }) {
  const [code, setCode] = useState('');
  const [fmt, setFmt] = useState('');

  useEffect(() => {
    if (cfg) { setCode(cfg.project_code || ''); setFmt(cfg.format || 'MA-{project_code}-{seq:04d}'); }
  }, [cfg, editing]);

  if (!cfg) return null;

  if (!editing) {
    return (
      <button onClick={onStart}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
        style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}
        title="Click to edit reference-ID format"
        data-testid="ma-ref-chip">
        <Hash className="w-2.5 h-2.5" />
        <span>Ref:</span>
        <code style={{ color: t.text }}>{cfg.format.replace('{project_code}', cfg.project_code).replace('{seq:04d}', 'NNNN').replace('{seq}', 'N')}</code>
        <Pencil className="w-2.5 h-2.5 opacity-60" />
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 text-[11px]" data-testid="ma-ref-editor">
      <span style={{ color: t.textDim }}>Code</span>
      <input value={code} onChange={e => setCode(e.target.value.toUpperCase())}
        className="rounded px-1.5 py-0.5 font-mono" size={8}
        style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
        data-testid="ma-ref-code" />
      <span style={{ color: t.textDim }}>Format</span>
      <input value={fmt} onChange={e => setFmt(e.target.value)}
        className="rounded px-1.5 py-0.5 font-mono"
        style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, width: 200 }}
        data-testid="ma-ref-format" />
      <button onClick={() => onSave({ project_code: code, format: fmt })}
        disabled={saving}
        className="p-1 rounded" style={{ background: t.accent, color: '#000' }}
        data-testid="ma-ref-save">
        {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />}
      </button>
      <button onClick={onCancel} className="p-1 rounded" style={{ background: t.inputBg, color: t.textMuted }} data-testid="ma-ref-cancel">
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

/* ══════════════ Ref-format chip + inline editor ══════════════ */
/* (section label moved; RefConfigChip is defined above) */

/* ══════════════ Location picker modal (pick existing OR create new) ══════════════ */
function LocationPickerModal({ t, row, tree, projectId, onClose, onSave, onTreeUpdated }) {
  const [zoneId, setZoneId] = useState(row.sln_zone_id || '');
  const [elementId, setElementId] = useState(row.sln_element_id || '');
  const [newZoneName, setNewZoneName] = useState('');
  const [newElemName, setNewElemName] = useState('');
  const [zoneMode, setZoneMode] = useState('pick');   // 'pick' | 'new'
  const [elemMode, setElemMode] = useState('pick');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const zone = tree.find(z => z.zone_id === zoneId);
  const elements = zone?.elements || [];

  useEffect(() => {
    if (tree.length === 0) setZoneMode('new');
  }, [tree.length]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      let finalZoneId = zoneId;
      let finalZoneName = tree.find(z => z.zone_id === zoneId)?.zone_name || '';

      // Create a new zone if requested
      if (zoneMode === 'new' && newZoneName.trim()) {
        const created = await fetchSln(`/api/sln/assets/${projectId}/zones`, {
          method: 'POST', body: JSON.stringify({ zone_name: newZoneName.trim() }),
        });
        finalZoneId = created.id;
        finalZoneName = created.zone_name;
      }

      let finalElemId = elementId;
      let finalElemName = elements.find(e => e.element_id === elementId)?.element_name || '';

      // Create a new element if requested
      if (elemMode === 'new' && newElemName.trim() && finalZoneId) {
        const created = await fetchSln(`/api/sln/zones/${finalZoneId}/elements`, {
          method: 'POST', body: JSON.stringify({ element_name: newElemName.trim() }),
        });
        finalElemId = created.id;
        finalElemName = created.element_name;
      }

      if (onTreeUpdated) await onTreeUpdated();

      await onSave({
        sln_zone_id:      finalZoneId || null,
        sln_zone_name:    finalZoneName || '',
        sln_element_id:   finalElemId || null,
        sln_element_name: finalElemName || '',
      });
    } catch (e) { setErr(e.message || 'Failed to save'); }
    setSaving(false);
  };

  const clear = async () => {
    setSaving(true);
    await onSave({ sln_zone_id: null, sln_zone_name: '', sln_element_id: null, sln_element_name: '' });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} data-testid="ma-location-modal">
      <div className="rounded-lg p-4 w-full max-w-md" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }} onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-bold mb-1">Material location</h3>
        <p className="text-[11px] mb-3" style={{ color: t.textDim }}>
          Where will this material be placed? Pick an existing zone/element or type a new one.
          Locations are shared with the Service Life module. Optional — leave blank if you haven't decided yet.
        </p>

        {/* ZONE */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Zone</label>
            {tree.length > 0 && (
              <button type="button" onClick={() => { setZoneMode(m => m === 'pick' ? 'new' : 'pick'); setZoneId(''); setNewZoneName(''); }}
                className="text-[10px] hover:underline" style={{ color: t.accent }} data-testid="ma-location-zone-toggle">
                {zoneMode === 'pick' ? '+ New zone' : '← Pick existing'}
              </button>
            )}
          </div>
          {zoneMode === 'pick' ? (
            <select value={zoneId} onChange={e => { setZoneId(e.target.value); setElementId(''); setElemMode('pick'); }}
              className="w-full rounded px-2 py-1 text-[12px] mt-1"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="ma-location-zone">
              <option value="">— pick zone —</option>
              {tree.map(z => (
                <option key={z.zone_id} value={z.zone_id}>{z.zone_name}{z.zone_code ? ` (${z.zone_code})` : ''}</option>
              ))}
            </select>
          ) : (
            <input type="text" value={newZoneName} onChange={e => setNewZoneName(e.target.value)}
              placeholder='e.g. "Pier 1", "Level 2", "Span 3 Deck"'
              className="w-full rounded px-2 py-1 text-[12px] mt-1"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="ma-location-zone-new" />
          )}
        </div>

        {/* ELEMENT (only when zone chosen or being created) */}
        {(zoneId || newZoneName.trim()) && (
          <div className="mb-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Structural Element (optional)</label>
              {zoneMode === 'pick' && elements.length > 0 && (
                <button type="button" onClick={() => { setElemMode(m => m === 'pick' ? 'new' : 'pick'); setElementId(''); setNewElemName(''); }}
                  className="text-[10px] hover:underline" style={{ color: t.accent }} data-testid="ma-location-elem-toggle">
                  {elemMode === 'pick' ? '+ New element' : '← Pick existing'}
                </button>
              )}
            </div>
            {elemMode === 'pick' && zoneMode === 'pick' ? (
              <select value={elementId} onChange={e => setElementId(e.target.value)}
                className="w-full rounded px-2 py-1 text-[12px] mt-1"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
                data-testid="ma-location-element">
                <option value="">— leave blank —</option>
                {elements.map(e => (
                  <option key={e.element_id} value={e.element_id}>{e.element_name}{e.element_type ? ` — ${e.element_type}` : ''}</option>
                ))}
              </select>
            ) : (
              <input type="text" value={newElemName} onChange={e => setNewElemName(e.target.value)}
                placeholder='e.g. "Column C7", "Edge beam", "Deck underside" — optional'
                className="w-full rounded px-2 py-1 text-[12px] mt-1"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
                data-testid="ma-location-element-new" />
            )}
            {elemMode === 'pick' && elements.length === 0 && zoneMode === 'pick' && (
              <p className="text-[10px] mt-1" style={{ color: t.textDim }}>No elements under this zone — switch to "+ New element" to add one.</p>
            )}
          </div>
        )}

        {err && <div className="text-[11px] mb-2 px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>{err}</div>}

        <div className="flex items-center justify-between mt-4 gap-2">
          {(row.sln_zone_id || row.sln_element_id) ? (
            <button onClick={clear} disabled={saving}
              className="text-[11px] px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171' }}
              data-testid="ma-location-clear">Clear location</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[11px] px-3 py-1 rounded" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
            <button onClick={submit}
              disabled={saving || (zoneMode === 'pick' && !zoneId && !newZoneName.trim())}
              className="text-[11px] px-3 py-1 rounded font-semibold disabled:opacity-50"
              style={{ background: t.accent, color: '#000' }}
              data-testid="ma-location-save">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
   ThemedSelect — native <select> elements on some OSs ignore the page
   color-scheme for their pop-up list, so the Section / Clause dropdown
   in the grid ends up with white-on-white rendering in dark mode. This
   lightweight controlled dropdown renders a button + positioned list
   using the current theme palette so it always looks right.
   ──────────────────────────────────────────────────────────────────── */
function ThemedSelect({ t, value, options, onCommit, onCancel, testid }) {
  const [open, setOpen] = useState(true);
  const wrapRef = useRef(null);
  const selected = options.find(o => o.code === value);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        onCancel && onCancel();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onCancel]);

  return (
    <div ref={wrapRef} className="relative" style={{ fontSize: 12 }} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-2 py-0.5 truncate flex items-center gap-1"
        style={{
          background: t.inputBg,
          border: `1px solid ${t.accent}`,
          color: t.text,
          borderRadius: 3,
          fontSize: 12,
          outline: 'none',
        }}>
        <span className="flex-1 truncate">{selected?.label || '—'}</span>
        <ChevronDown className="w-3 h-3" style={{ color: t.textMuted }} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 mt-1 overflow-y-auto shadow-xl"
          style={{
            background: t.card,
            border: `1px solid ${t.cardBorder}`,
            color: t.text,
            borderRadius: 4,
            maxHeight: 320,
            zIndex: 70,
            // The pop-up widens beyond the narrow cell so full labels fit
            // (e.g. "552-A · Class A — General structural") — no horizontal
            // scroll bar required.
            minWidth: 360,
            whiteSpace: 'nowrap',
            // Slightly smaller font than the surrounding grid for a classic
            // "dropdown density" look.
            fontSize: 11,
          }}>
          {options.map(opt => {
            const isSel = opt.code === value;
            return (
              <li
                key={opt.code || '_empty'}
                role="option"
                aria-selected={isSel}
                onMouseDown={(e) => { e.preventDefault(); onCommit(opt.code); setOpen(false); }}
                className="px-2 py-1 cursor-pointer"
                style={{
                  background: isSel ? t.accentBg : 'transparent',
                  color: isSel ? t.accent : t.text,
                  // Ensure the row itself doesn't wrap — label column grows
                  // horizontally and the whole pop-up scrolls only if the
                  // option count exceeds the max-height.
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = t.cardHover; }}
                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                {opt.label}
              </li>
            );
          })}
          {options.length === 0 && (
            <li className="px-2 py-1" style={{ color: t.textDim }}>No options</li>
          )}
        </ul>
      )}
    </div>
  );
}
